import { apiClient } from './apiClient';
import {
  ClientAccessMutationResult,
  ClientUserAccessList,
} from '../types/clientAccess';

/**
 * Manages who reaches provisioning for a given client, on the environment
 * this frontend is pointed at. The backend derives the environment from its
 * own SSO client, so the caller never passes it.
 */
class ClientAccessService {
  /**
   * List the users attached to a client with their access state.
   *
   * @param clientId - Client UUID
   * @param includeRevoked - Keep users whose access is currently revoked so
   *   they can be re-activated. Defaults to true, which is what the client
   *   detail screen wants.
   */
  async fetchClientUsers(
    clientId: string,
    includeRevoked: boolean = true,
  ): Promise<ClientUserAccessList> {
    try {
      const url = `/v2/clients/${encodeURIComponent(clientId)}/users?include_revoked=${includeRevoked}`;
      return await apiClient.get(url);
    } catch (error) {
      console.error('[CLIENT ACCESS] Failed to fetch client users:', error);
      throw error;
    }
  }

  /**
   * How many users each visible client has, in one call.
   *
   * Counting means reading group membership per client, so this is done
   * server-side rather than firing a request per table row.
   */
  async fetchUserCounts(): Promise<Record<string, number>> {
    try {
      const response = await apiClient.get('/v2/clients/user-counts');
      return response?.counts ?? {};
    } catch (error) {
      // A missing count must not break the clients list.
      console.error('[CLIENT ACCESS] Failed to fetch user counts:', error);
      return {};
    }
  }

  /** Activate a user's access to this client on this environment. */
  async grantAccess(clientId: string, userId: string): Promise<ClientAccessMutationResult> {
    try {
      return await apiClient.put(
        `/v2/clients/${encodeURIComponent(clientId)}/users/${encodeURIComponent(userId)}/access`,
      );
    } catch (error) {
      console.error('[CLIENT ACCESS] Failed to grant access:', error);
      throw error;
    }
  }

  /** Deactivate a user's access to this client on this environment. */
  async revokeAccess(clientId: string, userId: string): Promise<ClientAccessMutationResult> {
    try {
      return await apiClient.delete(
        `/v2/clients/${encodeURIComponent(clientId)}/users/${encodeURIComponent(userId)}/access`,
      );
    } catch (error) {
      console.error('[CLIENT ACCESS] Failed to revoke access:', error);
      throw error;
    }
  }

  /** Flip a user's access to the requested state. */
  async setAccess(
    clientId: string,
    userId: string,
    hasAccess: boolean,
  ): Promise<ClientAccessMutationResult> {
    return hasAccess
      ? this.grantAccess(clientId, userId)
      : this.revokeAccess(clientId, userId);
  }
}

export const clientAccessService = new ClientAccessService();
